// Statements: sentences that sit above Goals in the plan, grouped by the user's categories and linked
// to goals many-to-many (INTENT.md, "Plan"). Vision statements and Fears share one page, one editor
// and one service; this is everything that differs between them.
//
// The link is stored on both sides and always written in one batch: the statement's `goalIds`, and
// the goal's `goalField` (`visionIds` or `fearIds`).
export const STATEMENT_KINDS = {
  visions: {
    collection: "visions", goalField: "visionIds",
    // Visions were stored with `text` and a copy of the category's name before Fears existed.
    textField: "text", labelField: "visionCategoryLabel",
    uiKey: "vision", pageTitle: "My Vision", one: "vision statement", many: "vision statements",
    newTitle: "New vision statement", editTitle: "Edit vision statement", textLabel: "Vision statement",
    placeholder: "e.g. Be financially independent and afford the life I want",
    intro: "Vision statements describe who you want to be, how you want to live, and where you want to end up. Tap one to see the goals linked to it.",
    empty: "You haven't written a vision statement yet. A vision statement describes who you want to be, how you want to live, or where you want to end up.",
    goalsLabel: "Goals this connects to",
    // Internal wording for the goal suggestions; never shown.
    suggestAs: "vision statement",
    inspiration: [
      { cat: "Career & purpose",   items: ["Have a fulfilling career and job", "Have a job I'm excited to go to every day"] },
      { cat: "Financial",          items: ["Be financially independent", "Retire and still afford the life I want", "Have enough money to never worry about it"] },
      { cat: "Health & wellbeing", items: ["Be pain-free and sharp-minded in old age", "Be healthy as I age with few ailments", "Get ample, restful sleep every night", "Look good — right weight, well looked after"] },
      { cat: "Relationships",      items: ["Be a good husband and partner", "Be a great parent", "Be the friend everyone wants to spend time with", "Spend quality time with family"] },
      { cat: "Home & lifestyle",   items: ["Live in a house I love", "Enjoy cooking and eating healthy, delicious food", "Have lots of hobbies and ways to have fun", "Dress well"] },
      { cat: "Personal growth",    items: ["Live a life of fulfilment, happiness, and joy", "Be curious and keep learning"] },
    ],
  },
  fears: {
    collection: "fears", goalField: "fearIds",
    // Fears were stored with `title` when they were a plain list (parked/fears-simple-list).
    textField: "title", labelField: null,
    uiKey: "fears", pageTitle: "Fears", one: "fear", many: "fears",
    newTitle: "New fear", editTitle: "Edit fear", textLabel: "What are you afraid will happen?",
    placeholder: "e.g. Reach retirement without enough saved to enjoy it",
    intro: "These are the things you're afraid will happen if you keep putting things off. Tap a fear to see the goals that will keep it from happening.",
    empty: "You haven't written down any fears yet. Add something you're afraid will happen if you keep putting things off.",
    goalsLabel: "Goals that will keep this from happening",
    suggestAs: "fear about the future",
    inspiration: [
      { cat: "Career & purpose",   items: ["Still be in a job I don't like ten years from now", "Look back and wish I'd started my own thing"] },
      { cat: "Financial",          items: ["Not have enough saved to retire when I want to", "Always be one surprise bill away from trouble"] },
      { cat: "Health & wellbeing", items: ["Lose my health because I kept putting off exercise", "Be too tired to enjoy the things I love"] },
      { cat: "Relationships",      items: ["Miss my kids growing up because work always came first", "Drift apart from my closest friends"] },
      { cat: "Home & lifestyle",   items: ["Never get around to making my home the way I want it", "Keep saying \"next year\" about the trips I want to take"] },
      { cat: "Personal growth",    items: ["Look back and regret the things I never tried", "Stay stuck in the same habits I've had for years"] },
    ],
  },
};

const GENERAL = "General";

export const statementText = (kind, s) => s[STATEMENT_KINDS[kind].textField] || "";

// The group a statement shows under: its category's current name, or the name stored with it when
// the category has gone, or "General" when it has none.
export const groupLabel = (kind, s, categories = []) => {
  const cat = s.categoryId && categories.find(c => c.id === s.categoryId);
  if (cat) return cat.label;
  const stored = STATEMENT_KINDS[kind].labelField && s[STATEMENT_KINDS[kind].labelField];
  return stored || GENERAL;
};

// The groups that have something in them, in the categories' order and then any others (General, or a
// category that has gone). A category with nothing in it is left out. Each group is in the user's order.
export const groupStatements = (kind, statements, categories = []) => {
  const map = {};
  categories.forEach(c => { map[c.label] = []; });
  statements.forEach(s => {
    const key = groupLabel(kind, s, categories);
    (map[key] ||= []).push(s);
  });
  Object.keys(map).forEach(key => { if (!map[key].length) delete map[key]; });
  Object.values(map).forEach(arr => arr.sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity)));
  return map;
};

// Every statement, group by group as groupStatements orders them (Radar's Fears, the task editor's Fear field).
export const statementsInOrder = (kind, statements, categories = []) =>
  Object.values(groupStatements(kind, statements, categories || [])).flat();

// "1 fear", "3 vision statements"
export const statementCount = (kind, n) => `${n} ${n === 1 ? STATEMENT_KINDS[kind].one : STATEMENT_KINDS[kind].many}`;
